import {DataFrame, isColumnTypeNumerical} from 'data-frames/DataFrameTypes';
import {AnalyticsVisualizationAggregationType as AggregationType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {
  AGGREGATION_TYPE_TO_READABLE,
  INSIGHTS_LEGEND_LOCATION_TO_CHARTING_ENUM,
} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {PieChartConfig, PieChartProps, PIE_CHART_VALIDATION_MAP} from 'ui-library/charting/PieChart';

import cleanAndValidateDataFrame from './cleanAndValidateDataFrame';
import ConvertToChartPropsException from './ConvertToChartPropsException';
import maybeConvertProps from './maybeConvertProps';
import {ColumnInfo, ConversionOutput} from './PropConverterTypes';
import findAxisColumn from './utils/findAxisColumn';
import {getDataFrameColumnsMapping} from './utils/getDataFrameColumnsMapping';

type PieChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationPieChartParametersNode'
>;

export const ERROR_MESSAGES = {
  INVALID_COLUMNS_MAPPING: 'Invalid chart configuration',
  INVALID_MEASURE_FOR_AGGREGATION: 'Measure column can only be non-numerical for "Count" aggregations',
};

export interface ValidPieChartConversionOutput {
  valid: true;
  props: PieChartProps;
}

export function getPieChartColumnInfos(parameters: PieChartParameters): Array<ColumnInfo> {
  return [
    {
      chartDataColumnName: 'sliceName',
      inputDataColumnSystemName: parameters.sliceColumnName,
      typeValidationOptions: PIE_CHART_VALIDATION_MAP.sliceName,
      cleanupNulls: true,
    },
    {
      chartDataColumnName: 'measure',
      inputDataColumnSystemName: parameters.measureColumnName,
      typeValidationOptions: PIE_CHART_VALIDATION_MAP.measure,
      cleanupNulls: true,
    },
  ];
}

export function convertPieChartConfig(parameters: PieChartParameters): PieChartConfig {
  const {showPercentageInLabel, showSliceNameInLabel, showValueInLabel} = parameters;

  const legendLocation =
    parameters.legendLocation != null
      ? INSIGHTS_LEGEND_LOCATION_TO_CHARTING_ENUM[parameters.legendLocation]
      : null;

  return {
    sliceLabels: {
      showPercentage: showPercentageInLabel,
      showSliceName: showSliceNameInLabel,
      showValue: showValueInLabel,
    },
    ...(legendLocation != null ? {legend: {location: legendLocation}} : {}),
  };
}

export function cleanAndValidatePieChartDataFrame(
  dataFrame: DataFrame,
  parameters: PieChartParameters,
  columnInfos: Array<ColumnInfo>
): DataFrame {
  const cleanDataFrame = cleanAndValidateDataFrame(dataFrame, columnInfos, VisualizationType.PIE_CHART);
  const measureColumn = findAxisColumn(cleanDataFrame, parameters.measureColumnName);

  if (parameters.aggregationType == null) {
    throw new ConvertToChartPropsException('Pie chart has invalid aggregation');
  }

  if (parameters.aggregationType !== AggregationType.COUNT && !isColumnTypeNumerical(measureColumn.type)) {
    throw new ConvertToChartPropsException(
      `Pie charts with ${AGGREGATION_TYPE_TO_READABLE[
        parameters.aggregationType
      ].toLowerCase()} aggregation only support numerical measure columns`
    );
  }

  return cleanDataFrame;
}

export function convertPieChartProps(
  dataFrame: DataFrame,
  parameters: PieChartParameters
): ValidPieChartConversionOutput {
  const aggregationType = parameters.aggregationType ?? AggregationType.COUNT;
  const finalParameters = {
    ...parameters,
    aggregationType,
    measureColumnName:
      aggregationType === AggregationType.COUNT ? parameters.sliceColumnName : parameters.measureColumnName,
  };
  const columnInfos = getPieChartColumnInfos(finalParameters);
  const validatedDataFrame = cleanAndValidatePieChartDataFrame(dataFrame, finalParameters, columnInfos);

  return {
    valid: true,
    props: {
      columnsMapping: getDataFrameColumnsMapping(dataFrame, columnInfos),
      config: convertPieChartConfig(finalParameters),
      dataFrame: validatedDataFrame,
    },
  };
}

export default function maybeConvertPieChartProps(
  dataFrame: DataFrame,
  parameters: PieChartParameters
): ConversionOutput<PieChartProps> {
  return maybeConvertProps(() => convertPieChartProps(dataFrame, parameters));
}
